import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ThemeName, ThemeMode, Theme, themes, getTheme } from '@/config/themes';

interface ThemeContextType {
  themeName: ThemeName;
  themeMode: ThemeMode;
  theme: Theme;
  setThemeName: (name: ThemeName) => void;
  setThemeMode: (mode: ThemeMode) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEYS = {
  THEME_NAME: 'theme-name',
  THEME_MODE: 'theme-mode',
};

function applyTheme(theme: Theme, mode: 'light' | 'dark') {
  const root = document.documentElement;
  const colors = theme.colors[mode];
  
  root.style.setProperty('--background', colors.background);
  root.style.setProperty('--foreground', colors.foreground);
  root.style.setProperty('--card', colors.card);
  root.style.setProperty('--card-foreground', colors.cardForeground);
  root.style.setProperty('--popover', colors.popover);
  root.style.setProperty('--popover-foreground', colors.popoverForeground);
  root.style.setProperty('--primary', colors.primary);
  root.style.setProperty('--primary-foreground', colors.primaryForeground);
  root.style.setProperty('--secondary', colors.secondary);
  root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
  root.style.setProperty('--muted', colors.muted);
  root.style.setProperty('--muted-foreground', colors.mutedForeground);
  root.style.setProperty('--accent', colors.accent);
  root.style.setProperty('--accent-foreground', colors.accentForeground);
  root.style.setProperty('--destructive', colors.destructive);
  root.style.setProperty('--destructive-foreground', colors.destructiveForeground);
  root.style.setProperty('--border', colors.border);
  root.style.setProperty('--input', colors.input);
  root.style.setProperty('--ring', colors.ring);
  
  // Toggle dark class on html element
  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
  defaultMode?: ThemeMode;
}

export function ThemeProvider({ 
  children, 
  defaultTheme = 'default',
  defaultMode = 'system'
}: ThemeProviderProps) {
  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME_NAME);
    return (stored as ThemeName) || defaultTheme;
  });
  
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
    return (stored as ThemeMode) || defaultMode;
  });

  const theme = getTheme(themeName);

  // Apply theme when theme name or mode changes
  useEffect(() => {
    const effectiveMode = themeMode === 'system' ? getSystemTheme() : themeMode;
    applyTheme(theme, effectiveMode);
  }, [theme, themeMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme(theme, getSystemTheme());
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, themeMode]);

  const setThemeName = useCallback((name: ThemeName) => {
    localStorage.setItem(STORAGE_KEYS.THEME_NAME, name);
    setThemeNameState(name);
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);
    setThemeModeState(mode);
  }, []);

  const value: ThemeContextType = {
    themeName,
    themeMode,
    theme,
    setThemeName,
    setThemeMode,
    availableThemes: themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { ThemeContext };
